import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  Input,
  InputAdornment,
  makeStyles,
  Paper,
  Typography,
} from "@material-ui/core";
import SendIcon from "@material-ui/icons/Send";

import { AuthContext } from "../../context/Auth/AuthContext";
import { useDate } from "../../hooks/useDate";
import api from "../../services/api";

import AttachFileIcon from "@material-ui/icons/AttachFile";
import CancelIcon from "@material-ui/icons/Cancel";
import CircularProgress from "@material-ui/core/CircularProgress";
import ModalImageCors from "../../components/ModalImageCors";
import { GetApp } from "@material-ui/icons";
import toastError from "../../errors/toastError";
import MicRecorder from "mic-recorder-to-mp3";
import MicIcon from "@material-ui/icons/Mic";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import RecordingTimer from "../../components/MessageInputCustom/RecordingTimer";
import { green } from '@material-ui/core/colors';

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    position: "relative",
    flex: 1,
    overflow: "hidden",
    borderRadius: 0,
    height: "100%",
    borderLeft: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#E2E8F0"}`,
    backgroundColor: theme.palette.type === "dark" ? "#1E293B" : "#F8FAFC",
  },
  messageList: {
    position: "relative",
    overflowY: "auto",
    height: "100%",
    padding: theme.spacing(2),
    ...theme.scrollbarStyles,
  },
  inputArea: {
    position: "relative",
    height: "auto",
    borderTop: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#E2E8F0"}`,
    backgroundColor: theme.palette.type === "dark" ? "#0F172A" : "#FFFFFF",
  },
  input: {
    padding: theme.spacing(2),
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontSize: 14,
  },
  buttonSend: {
    margin: theme.spacing(1),
    color: "#35af9a",
    "&:hover": {
      backgroundColor: "rgba(53,175,154,0.1)",
    },
  },
  boxLeft: {
    padding: "10px 14px 8px 14px",
    margin: "8px 0 8px 0",
    position: "relative",
    backgroundColor: theme.palette.type === "dark" ? "#0F172A" : "#FFFFFF",
    color: theme.palette.type === "dark" ? "#F1F5F9" : "#1E293B",
    maxWidth: 300,
    borderRadius: 12,
    borderBottomLeftRadius: 4,
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#E2E8F0"}`,
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    "&::before": {
      content: '""',
      position: "absolute",
      bottom: 0,
      left: -8,
      width: 0,
      height: 0,
      borderTop: "8px solid transparent",
      borderRight: `8px solid ${theme.palette.type === "dark" ? "#0F172A" : "#FFFFFF"}`,
      borderBottom: "0px solid transparent",
    },
  },
  boxRight: {
    padding: "10px 14px 8px 14px",
    margin: "8px 0 8px auto",
    position: "relative",
    backgroundColor: theme.palette.type === "dark" ? "#005c4b" : "#dcf8c6",
    color: theme.palette.type === "dark" ? "#F1F5F9" : "#1E293B",
    textAlign: "right",
    maxWidth: 300,
    borderRadius: 12,
    borderBottomRightRadius: 4,
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#E2E8F0"}`,
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    "&::before": {
      content: '""',
      position: "absolute",
      bottom: 0,
      right: -8,
      width: 0,
      height: 0,
      borderTop: "8px solid transparent",
      borderLeft: `8px solid ${theme.palette.type === "dark" ? "#005c4b" : "#dcf8c6"}`,
      borderBottom: "0px solid transparent",
    },
  },
  messageText: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontSize: 14,
    lineHeight: 1.5,
    marginBottom: theme.spacing(0.5),
  },
  messageSender: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontWeight: 600,
    fontSize: 12,
    marginBottom: theme.spacing(0.5),
    color: theme.palette.type === "dark" ? "#94A3B8" : "#64748B",
  },
  messageTime: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontSize: 11,
    color: theme.palette.type === "dark" ? "#64748B" : "#94A3B8",
    marginTop: theme.spacing(0.5),
  },
  sendMessageIcons: {
    color: theme.palette.type === "dark" ? "#94A3B8" : "#64748B",
    "&:hover": {
      color: "#35af9a",
    },
  },
  uploadInput: {
    display: "none",
  },
  circleLoading: {
    color: green[500],
    opacity: "70%",
    position: "absolute",
    top: "20%",
    left: "50%",
    marginLeft: -12,
  },
  viewMediaInputWrapper: {
    display: "flex",
    padding: theme.spacing(1.5, 2),
    position: "relative",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: theme.palette.type === "dark" ? "#1E293B" : "#F8FAFC",
    borderTop: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#E2E8F0"}`,
  },
  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "inherit",
    padding: theme.spacing(1.5),
  },
  messageMedia: {
    objectFit: "cover",
    width: 250,
    height: 200,
    borderRadius: 8,
    marginBottom: theme.spacing(1),
  },
  recorderWrapper: {
    display: "flex",
    alignItems: "center",
    alignContent: "middle",
    justifyContent: 'flex-end',
    padding: theme.spacing(1.5),
  },
  cancelAudioIcon: {
    color: "#EF4444",
    "&:hover": {
      backgroundColor: "rgba(239,68,68,0.1)",
    },
  },
  audioLoading: {
    color: green[500],
    opacity: "70%",
  },
  sendAudioIcon: {
    color: "#35af9a",
    "&:hover": {
      backgroundColor: "rgba(53,175,154,0.1)",
    },
  },
  downloadButton: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontWeight: 600,
    fontSize: 13,
    textTransform: "none",
    borderRadius: 8,
    borderColor: "#35af9a",
    color: "#35af9a",
    "&:hover": {
      borderColor: "#2d9a87",
      backgroundColor: "rgba(53,175,154,0.1)",
    },
  },
}));

const Mp3Recorder = new MicRecorder({ bitRate: 128 });

export default function ChatMessages({
  chat,
  messages,
  handleSendMessage,
  handleLoadMore,
  scrollToBottomRef,
  pageInfo,
}) {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { datetimeToClient } = useDate();
  const baseRef = useRef();

  const [contentMessage, setContentMessage] = useState("");
  const [medias, setMedias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);

  const scrollToBottom = () => {
    if (baseRef.current) {
      baseRef.current.scrollIntoView({});
    }
  };

  const unreadMessages = (chat) => {
    if (chat !== undefined) {
      const currentUser = chat.users.find((u) => u.userId === user.id);
      return currentUser.unreads > 0;
    }
    return 0;
  };

  useEffect(() => {
    if (unreadMessages(chat) > 0) {
      try {
        api.post(`/chats/${chat.id}/read`, { userId: user.id });
      } catch (err) {}
    }
    scrollToBottomRef.current = scrollToBottom;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScroll = (e) => {
    const { scrollTop } = e.currentTarget;
    if (!pageInfo.hasMore || loading) return;
    if (scrollTop < 600) {
      handleLoadMore();
    }
  };

  const handleChangeMedias = (e) => {
    if (!e.target.files) {
      return;
    }
    const selectedMedias = Array.from(e.target.files);
    setMedias(selectedMedias);
  };
  
  const checkMessageMedia = (message) => {
    if (message.mediaType === "image") {
      return <ModalImageCors imageUrl={message.mediaPath} />;
    }
    if (message.mediaType === "audio") {
      return (
        <audio controls style={{ width: "100%", marginBottom: 8 }}>
          <source src={message.mediaPath} type="audio/ogg"></source>
        </audio>
      );
    }
    if (message.mediaType === "video") {
      return (
        <video
          className={classes.messageMedia}
          src={message.mediaPath}
          controls
        />
      );
    } else {
      return (
        <>
          <div className={classes.downloadMedia}>
            <Button
              startIcon={<GetApp />}
              className={classes.downloadButton}
              variant="outlined"
              target="_blank"
              href={message.mediaPath}
            >
              Download
            </Button>
          </div>
        </>
      );
    }
  };
  
  const handleSendMedia = async (e) => {
    setLoading(true);
    e.preventDefault();
    const currentChat = JSON.parse(localStorage.getItem("currentChat"));
    const formData = new FormData();
    formData.append("fromMe", true);
    formData.append("typeArch","chats");
    medias.forEach((media) => {
      formData.append("medias", media);
      formData.append("body", media.name);
    });
    try {
      await api.post(`/chats/${currentChat.id}/messages`, formData);
    } catch (err) {
      console.log(err);
      toastError(err);
    }
    setLoading(false);
    setMedias([]);
  };
  
  const handleStartRecording = async () => {
    setLoading(true);
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      await Mp3Recorder.start();
      setRecording(true);
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };
  
  const handleUploadAudio = async (e) => {
    setLoading(true);
    e.preventDefault();
    const [, blob] = await Mp3Recorder.stop().getMp3();
    if (blob.size < 10000) {
      setLoading(false);
      setRecording(false);
      return;
    }
    const formData1 = new FormData();
    formData1.append("fromMe", true);
    formData1.append("typeArch","chats");
    const currentChat = JSON.parse(localStorage.getItem("currentChat"));
    const filename = `audio-${new Date().getTime()}.mp3`;
    formData1.append("medias", blob, filename);
    formData1.append("body", filename);
    try {
      await api.post(`/chats/${currentChat.id}/messages`, formData1);
    } catch (err) {
      toastError(err);
    }
    setRecording(false);
    setLoading(false);
  };
  
  const handleCancelAudio = async () => {
    try {
      await Mp3Recorder.stop().getMp3();
      setRecording(false);
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Paper className={classes.mainContainer} elevation={0}>
      <div onScroll={handleScroll} className={classes.messageList}>
        {Array.isArray(messages) &&
          messages.map((item, key) => {
            if (item.senderId === user.id) {
              return (
                <Box key={key} className={classes.boxRight}>
                  <Typography className={classes.messageSender}>
                    {item.sender.name}
                  </Typography>
                  {item.mediaPath && checkMessageMedia(item)}
                  <Typography className={classes.messageText}>
                    {item.message}
                  </Typography>
                  <Typography className={classes.messageTime} display="block">
                    {datetimeToClient(item.createdAt)}
                  </Typography>
                </Box>
              );
            } else {
              return (
                <Box key={key} className={classes.boxLeft}>
                  <Typography className={classes.messageSender}>
                    {item.sender.name}
                  </Typography>
                  {item.mediaPath && checkMessageMedia(item)}
                  <Typography className={classes.messageText}>
                    {item.message}
                  </Typography>
                  <Typography className={classes.messageTime} display="block">
                    {datetimeToClient(item.createdAt)}
                  </Typography>
                </Box>
              );
            }
          })}
        <div ref={baseRef}></div>
      </div>
      <div className={classes.inputArea}>
        <FormControl variant="outlined" fullWidth>
        {recording ? (
            <div className={classes.recorderWrapper}>
              <IconButton
                aria-label="cancelRecording"
                component="span"
                fontSize="large"
                disabled={loading}
                onClick={handleCancelAudio}
                className={classes.cancelAudioIcon}
              >
                <HighlightOffIcon />
              </IconButton>
              {loading ? (
                <div>
                  <CircularProgress className={classes.audioLoading} size={24} />
                </div>
              ) : (
                <RecordingTimer />
              )}

              <IconButton
                aria-label="sendRecordedAudio"
                component="span"
                onClick={handleUploadAudio}
                disabled={loading}
                className={classes.sendAudioIcon}
              >
                <CheckCircleOutlineIcon />
              </IconButton>
            </div>
          )
            :
            <>
              {medias.length > 0 ?
                <>
                  <Paper elevation={0} square className={classes.viewMediaInputWrapper}>
                    <IconButton
                      aria-label="cancel-upload"
                      component="span"
                      onClick={(e) => setMedias([])}
                      className={classes.sendMessageIcons}
                    >
                      <CancelIcon />
                    </IconButton>

                    {loading ? (
                      <div>
                        <CircularProgress className={classes.circleLoading} size={20} />
                      </div>
                    ) : (
                      <Typography variant="body2" style={{ fontFamily: '"Inter", "Poppins", sans-serif' }}>
                        {medias[0]?.name}
                      </Typography>
                    )}
                    <IconButton
                      aria-label="send-upload"
                      component="span"
                      onClick={handleSendMedia}
                      disabled={loading}
                      className={classes.sendMessageIcons}
                    >
                      <SendIcon />
                    </IconButton>
                  </Paper>
                </>
                :
                <React.Fragment>
                  <Input
                    multiline
                    value={contentMessage}
                    onKeyUp={(e) => {
                      if (e.key === "Enter" && contentMessage.trim() !== "") {
                        handleSendMessage(contentMessage);
                        setContentMessage("");
                      }
                    }}
                    onChange={(e) => setContentMessage(e.target.value)}
                    className={classes.input}
                    placeholder="Digite uma mensagem..."
                    startAdornment={
                      <InputAdornment position="start">
                        <FileInput disableOption={loading} handleChangeMedias={handleChangeMedias} />
                      </InputAdornment>
                    }
                    endAdornment={
                      <InputAdornment position="end">
                        {contentMessage ? (
                          <IconButton
                            onClick={() => {
                              if (contentMessage.trim() !== "") {
                                handleSendMessage(contentMessage);
                                setContentMessage("");
                              }
                            }}
                            className={classes.buttonSend}
                          >
                            <SendIcon />
                          </IconButton>
                        ) : (
                          <IconButton
                            aria-label="showRecorder"
                            component="span"
                            disabled={loading}
                            onClick={handleStartRecording}
                            className={classes.sendMessageIcons}
                          >
                            <MicIcon />
                          </IconButton>
                        )}
                      </InputAdornment>
                    }
                  />
                </React.Fragment>
              }
            </>
          }
        </FormControl>
      </div>
    </Paper>
  );
}

const FileInput = (props) => {
  const { handleChangeMedias, disableOption } = props;
  const classes = useStyles();
  return (
    <>
      <input
        multiple
        type="file"
        id="upload-button"
        disabled={disableOption}
        className={classes.uploadInput}
        onChange={handleChangeMedias}
      />
      <label htmlFor="upload-button">
        <IconButton
          aria-label="upload"
          component="span"
          disabled={disableOption}
          className={classes.sendMessageIcons}
        >
          <AttachFileIcon />
        </IconButton>
      </label>
    </>
  );
};
